/**
 * PersonLink — renders an avatar + display-name for a user UID.
 *
 * Lives in @praesto/core so any module (shifts, procedure, monitoring) can
 * import it without violating the cross-module import rule (CLAUDE.md root §2).
 *
 * Resolution strategy (in order):
 *  1. Props `displayName`/`photoUrl` — caller already has the data, used immediately.
 *  2. Module-level cache — survives re-renders within a page session.
 *  3. Injected resolver — registered at app boot via `setPersonResolver()`.
 *
 * Usage:
 *   // At app boot (apps/web/src/App.tsx or similar):
 *   setPersonResolver(async (uid) => { ... });
 *
 *   // In a route component:
 *   <PersonLink uid={shift.assignedUserId} tenantSlug={tenantSlug} size={24} />
 */
import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { AvatarOrInitials } from "./AvatarOrInitials.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PersonInfo {
  displayName: string;
  photoUrl?: string;
}

export interface PersonLinkProps {
  uid: string;
  /** If provided, used directly without fetching. */
  displayName?: string;
  photoUrl?: string;
  /** Skip the <Link> wrapper (e.g. audit-only display or unknown tenantSlug). */
  noLink?: boolean;
  size?: 24 | 32;
  /** Tenant slug for the URL. If absent, no link is rendered (noLink fallback). */
  tenantSlug?: string;
}

// ---------------------------------------------------------------------------
// Resolver registry + cache
// ---------------------------------------------------------------------------

let resolver: ((uid: string) => Promise<PersonInfo | undefined>) | null = null;

/**
 * Wire this once at app boot so PersonLink can lazily resolve display names.
 * Example:
 *   setPersonResolver(async (uid) => {
 *     const users = await core.organization.users();
 *     const u = users.find(x => x.id === uid);
 *     if (!u) return undefined;
 *     return { displayName: u.displayName ?? u.email, ...(u.photoUrl ? { photoUrl: u.photoUrl } : {}) };
 *   });
 */
export function setPersonResolver(fn: (uid: string) => Promise<PersonInfo | undefined>): void {
  resolver = fn;
}

const cache = new Map<string, PersonInfo>();

/**
 * Evict one cached entry (or clear all if no uid given).
 * Call when a user.updated event fires so stale names are refreshed.
 */
export function invalidatePersonCache(uid?: string): void {
  if (uid) cache.delete(uid);
  else cache.clear();
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PersonLink({
  uid,
  displayName,
  photoUrl,
  noLink,
  size = 24,
  tenantSlug,
}: PersonLinkProps) {
  const seeded: PersonInfo | undefined = displayName
    ? { displayName, ...(photoUrl ? { photoUrl } : {}) }
    : undefined;

  const [info, setInfo] = useState<PersonInfo | undefined>(() => seeded ?? cache.get(uid));

  useEffect(() => {
    if (info || !uid || !resolver) return;
    let cancelled = false;
    resolver(uid)
      .then((r) => {
        if (cancelled || !r) return;
        cache.set(uid, r);
        setInfo(r);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [uid, info]);

  const label = info?.displayName ?? `${uid.slice(0, 8)}…`;

  const avatarProps = info?.photoUrl
    ? { displayName: label, photoUrl: info.photoUrl, size }
    : { displayName: label, size };

  const content = (
    <>
      {/* Monochrome by design — see AvatarOrInitials: color is rationed. */}
      <AvatarOrInitials {...avatarProps} />
      <span className="text-sm">{label}</span>
    </>
  );

  if (noLink || !tenantSlug) {
    return <span className="inline-flex items-center gap-1.5">{content}</span>;
  }

  return (
    <Link
      to={`/${tenantSlug}/people/${uid}` as never}
      className="inline-flex items-center gap-1.5 rounded-xs hover:underline focus-visible:outline-none focus-visible:shadow-focus"
    >
      {content}
    </Link>
  );
}
